import { COLOR_PICKER_POPUP_MARGIN, COLOR_PICKER_POPUP_TOTAL_HEIGHT, ColorPickerPopup, IColorPickerPopupPosition } from "components/colorpicker/popup/ColorPickerPopup";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTableBuilderContext } from "../../TableBuilderContext";
import { TWebsiteBuilderTableRow } from "../../../nodes/container/websiteBuilderTable";
import { TTableBuilderSetRowColorProperty } from "../../hooks/useTableBuilderOperations";

type TTableBuilderRowColorPickerInitialize = () => void;

interface ITableBuilderRowColorPickerState {
  position: IColorPickerPopupPosition;
  color: string;
}

export function TableBuilderRowColorPicker() {
  const { selection, domNodeRefMap, operations, tableNode } = useTableBuilderContext();

  const initializeRef = useRef<TTableBuilderRowColorPickerInitialize>(null);
  const debouncedColorTimerRef = useRef<NodeJS.Timeout>(null);

  const [state, setState] = useState<ITableBuilderRowColorPickerState>(null);

  const open = selection?.type === "row" && selection.colorPickerType !== "none";

  const tableNodeSettings = tableNode.container.settings;

  const tableNodeRows: TWebsiteBuilderTableRow[] = useMemo(() => {
    return [tableNodeSettings.head.row, ...tableNodeSettings.body.rows];
  }, [
    tableNodeSettings.head.row,
    tableNodeSettings.body.rows,
  ]);

  const { selectedRow, selectedRowColorProperty, selectedRowColor } = useMemo(() => {
    let selectedRow: TWebsiteBuilderTableRow = null;
    let selectedRowColorProperty: TTableBuilderSetRowColorProperty = null;
    let selectedRowColor: string = null;

    if (!open) {
      return {
        selectedRow,
        selectedRowColorProperty,
        selectedRowColor,
      };
    } else {
      selectedRow = tableNodeRows.find(row => row.id === selection.id);
      selectedRowColorProperty = selection.colorPickerType === "background-color"
        ? "backgroundColor"
        : "color";
      selectedRowColor = selectedRow == null ? null : selectedRow[selectedRowColorProperty];

      return {
        selectedRow,
        selectedRowColorProperty,
        selectedRowColor,
      };
    }
  }, [open, tableNodeRows, selection]);

  initializeRef.current = () => {
    if (!open) {
      setState(null);
      return;
    }

    const domNode = domNodeRefMap.current.get(selection.id);
    const boundingRect = domNode?.getBoundingClientRect();

    if (boundingRect == null) {
      setState(null);
      return;
    }

    const x = boundingRect.left + (boundingRect.width / 2);
    let y = window.innerHeight - COLOR_PICKER_POPUP_TOTAL_HEIGHT;

    const topY = boundingRect.top - COLOR_PICKER_POPUP_TOTAL_HEIGHT;
    const bottomY = boundingRect.bottom + COLOR_PICKER_POPUP_TOTAL_HEIGHT;

    if (bottomY <= window.innerHeight) {
      y = boundingRect.bottom + COLOR_PICKER_POPUP_MARGIN;
    } else if (topY >= 0) {
      y = topY;
    }

    setState({
      position: { x, y },
      color: selectedRowColor,
    });
  };

  useLayoutEffect(() => {
    if (open) {
      initializeRef.current();
    } else {
      setState(null);
      clearTimeout(debouncedColorTimerRef.current);
    }
  }, [open]);

  function updateColor(updatedColor: string) {
    clearTimeout(debouncedColorTimerRef.current);

    debouncedColorTimerRef.current = setTimeout(() => {
      if (selectedRow == null || selectedRowColorProperty == null) {
        return;
      }

      operations.setRowColor(selectedRow.id, selectedRowColorProperty, updatedColor);
    }, 500);

    setState(prev => ({
      ...prev,
      color: updatedColor,
    }));
  }

  function closePopup() {
    operations.setSelection(null);
  }

  if (open && state != null) {
    return (
      <ColorPickerPopup
        position={state.position}
        hexColor={state.color}
        updateHexColor={updateColor}
        closePopup={closePopup}
        portalParentId={WEBSITE_BUILDER_PORTAL}
      />
    );
  } else {
    return null;
  }
}